import { useState, useEffect, useRef, type FormEvent } from 'react'
import type { HandoverContact } from '@/shared/types'
import { Button } from '@/components/ui/button'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'

export interface ContactFields {
  name: string
  role: string
  email: string
  phone: string
  linkedin: string
}

interface Props {
  // null = "add new contact" mode; otherwise we're editing an existing row.
  initial?: HandoverContact | null
  mode: 'add' | 'edit'
  // Add mode, but the contact is already on the card and has no row to PATCH
  // (it came from the AI brief alone). The save path is the add path; only the
  // wording changes, so the BDR who clicked Edit is not told they are creating
  // someone new.
  adopting?: boolean
  // Display label for the modal title (the company name, typically).
  leadCompany?: string
  onSave: (fields: Partial<ContactFields>) => void | Promise<void>
  onCancel: () => void
  // Edit-mode only: when provided, renders a "Remove contact" link on the
  // left of the footer. The parent owns the confirm prompt and the actual
  // delete call; this component just exposes the affordance.
  onDelete?: () => void | Promise<void>
}

const FIELD_LABELS: Record<keyof ContactFields, string> = {
  name: 'Namn',
  role: 'Roll / titel',
  email: 'E-post',
  phone: 'Telefon',
  linkedin: 'LinkedIn',
}

// Modal-shaped editor used for both adding and editing a contact. Lives on
// top of the HandoverCard rather than swapping inline so the contact rows
// keep their tight layout when not being edited. Saves only the fields the
// BDR actually changed (edit) or filled in (add) so the API call stays
// minimal.
export default function ContactEditor({
  initial, mode, adopting = false, leadCompany, onSave, onCancel, onDelete,
}: Props) {
  const [fields, setFields] = useState<ContactFields>(() => ({
    name: initial?.name && initial.name !== '(unnamed)' ? initial.name : '',
    role: initial?.role || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    linkedin: initial?.linkedin || '',
  }))
  const [saving, setSaving] = useState(false)
  const firstInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    firstInputRef.current?.focus()
    firstInputRef.current?.select()
  }, [])

  const setField = (k: keyof ContactFields, v: string) =>
    setFields(prev => ({ ...prev, [k]: v }))

  const submit = async (e?: FormEvent) => {
    e?.preventDefault()
    if (saving) return
    if (mode === 'add' && !fields.name.trim()) {
      firstInputRef.current?.focus()
      return
    }

    let toSend: Partial<ContactFields>
    if (mode === 'add') {
      // Strip blanks so we don't POST a wall of empty strings.
      toSend = Object.fromEntries(
        (Object.entries(fields) as [keyof ContactFields, string][])
          .filter(([, v]) => v.trim() !== '')
          .map(([k, v]) => [k, v.trim()]),
      ) as Partial<ContactFields>
    } else {
      // PATCH only what actually changed vs. the row we started from.
      const before: ContactFields = {
        name: initial?.name && initial.name !== '(unnamed)' ? initial.name : '',
        role: initial?.role || '',
        email: initial?.email || '',
        phone: initial?.phone || '',
        linkedin: initial?.linkedin || '',
      }
      toSend = {}
      ;(Object.keys(fields) as (keyof ContactFields)[]).forEach(k => {
        if (fields[k].trim() !== before[k].trim()) {
          toSend[k] = fields[k].trim()
        }
      })
      if (Object.keys(toSend).length === 0) {
        onCancel()
        return
      }
    }

    setSaving(true)
    try {
      await onSave(toSend)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!onDelete || saving) return
    setSaving(true)
    try {
      await onDelete()
    } finally {
      setSaving(false)
    }
  }

  const title = mode === 'add' && !adopting
    ? <>Lägg till kontakt{leadCompany ? <> · <span className="text-ink-500 font-normal">{leadCompany}</span></> : null}</>
    : <>Redigera {initial?.name || 'kontakt'}</>

  return (
    <Dialog open onOpenChange={next => { if (!next) onCancel() }}>
      <DialogContent size="sm">
        <form
          onSubmit={submit}
          onKeyDown={e => { if (e.key === 'Escape') onCancel() }}
          className="contents"
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <FieldGroup>
              <div className="grid grid-cols-2 gap-3">
                {(Object.keys(FIELD_LABELS) as (keyof ContactFields)[]).map((k, i) => (
                  <Field key={k} className={k === 'linkedin' ? 'col-span-2' : undefined}>
                    <FieldLabel htmlFor={`contact-${k}`} required={mode === 'add' && k === 'name'}>
                      {FIELD_LABELS[k]}
                    </FieldLabel>
                    <Input
                      id={`contact-${k}`}
                      ref={i === 0 ? firstInputRef : undefined}
                      type="text"
                      value={fields[k]}
                      onChange={e => setField(k, e.target.value)}
                    />
                  </Field>
                ))}
              </div>
            </FieldGroup>
          </DialogBody>
          <DialogFooter className="sm:justify-between">
            {onDelete && mode === 'edit' ? (
              <Button type="button" variant="link" onClick={handleDelete} disabled={saving} className="text-ink-500 hover:text-[var(--color-danger)]">
                Ta bort kontakt
              </Button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
                Avbryt
              </Button>
              <Button
                type="submit"
                variant="yellow"
                disabled={saving || (mode === 'add' && !fields.name.trim())}
                loading={saving}
              >
                {mode === 'add' && !adopting ? 'Lägg till kontakt' : 'Spara'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
